import classnames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { connect } from 'react-redux';
import { findProposal } from 'utils/utils';
import ChatCustomHeader from 'v2/Chat/ChatCustomHeader';
import {
  GenericObject,
  ProposalEntity,
  SearchEntity,
  TalkjsConversationEntity,
  User,
} from 'v2/models';
import styles from './chat.module.scss';
import { fetchAllProposals } from '../../../actions';
import { useHistory, useParams } from 'react-router-dom';
import restFactory from 'v2/utils/restFactory';
import { BsFillXOctagonFill } from 'react-icons/bs';
import { REVIEW_STATUS } from 'v2/utils/searchUtils';

export interface Conversation {
  conversation: TalkjsConversationEntity;
  others: GenericObject;
}

interface StateProps {
  chatSession: GenericObject;
  user: { user: User; ghostUserEmail: string };
  proposals: {
    multipleProposals: ProposalEntity[];
    singleProposal: ProposalEntity;
  };
}

interface DispatchProps {
  // eslint-disable-next-line @typescript-eslint/ban-types
  fetchAllProposals: Function;
}
interface OwnProps {
  className?: string;
  search?: SearchEntity;
  disable_filter?: boolean;
}

type ChatProps = OwnProps & StateProps & DispatchProps;

const Chat = ({
  chatSession,
  user,
  disable_filter,
  search,
  proposals,
  fetchAllProposals,
  className,
}: ChatProps) => {
  const { proposalId } = useParams<{ proposalId: string }>();
  const history = useHistory();
  const [isClosed, setIsClosed] = useState(false);
  const [conversationInfo, setConversationInfo] = useState<Conversation>({
    conversation: {
      id: '',
      subject: '',
    },
    others: {},
  });
  const [proposal, setProposal] = useState<ProposalEntity>();
  const inbox = useRef<GenericObject>();
  const chatContainer = useRef() as React.MutableRefObject<HTMLInputElement>;
  const [hasConversations, setHasConversations] = useState(false);
  const [isHeaderReady, setIsHeaderReady] = useState(false);
  const [isIframeLoaded, setIsIframeLoaded] = useState(false);
  const [isConversationSelectedCalled, setIsConversationSelectedCalled] =
    useState(false);

  useEffect(() => {
    loadChat();
    return () => {
      if (inbox.current) {
        inbox.current.destroy();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loading = !(
    isIframeLoaded &&
    (isHeaderReady || !isConversationSelectedCalled)
  );

  const loadConversationFullData = async (id: string) => {
    return await restFactory.get<TalkjsConversationEntity>(
      `/conversations/${id}`
    );
  };

  const validateProposal = async (conversation: TalkjsConversationEntity) => {
    let proposal = null;
    if (user && conversation) {
      proposal = findProposal(
        proposals.multipleProposals,
        parseInt(conversation.custom?.proposal || '')
      );
    } else {
      proposal = proposals.singleProposal;
    }
    if (user.user.data_provider && proposal && !proposal.data_buyer_response) {
      proposal = await restFactory.get<ProposalEntity>(
        `/proposals/${proposal.id}`
      );
      let data_buyer;

      if (
        !proposal.data_buyer_child ||
        proposal.data_buyer_child.display_master_org_details
      ) {
        data_buyer = proposal.data_buyer_master;
      } else {
        data_buyer = proposal.data_buyer_child;
      }
      proposal = { ...proposal, data_buyer };
    }
    return proposal;
  };

  const getIsConversationClosed = (conversation: TalkjsConversationEntity) => {
    return Object.values(conversation.participants || {}).some(
      (item) => item.access === 'Read'
    );
  };

  const conversationSelected = async ({
    conversation,
    others,
  }: Conversation) => {
    if (!conversation) {
      return;
    }

    setIsConversationSelectedCalled(true);
    const newProposalId = conversation.custom?.proposal;
    if (proposalId && newProposalId !== proposalId) {
      history.push({
        pathname: `/discussions/${conversation.custom?.proposal}`,
      });
    }

    const proposal = await validateProposal(conversation);
    const conversationFullData = await loadConversationFullData(
      conversation.id
    );

    setHasConversations(true);
    setIsClosed(getIsConversationClosed(conversationFullData));
    setConversationInfo({ conversation: conversationFullData, others });
    setProposal(proposal);
  };

  const loadChat = async () => {
    if (user.ghostUserEmail) {
      return;
    }
    if (chatSession) {
      if (!inbox.current) {
        inbox.current = chatSession.createInbox({
          showChatHeader: false,
          showFeedHeader: false,
        });
        if (!user || user.user.data_provider) {
          inbox.current?.onConversationSelected(conversationSelected);
        }

        if (!disable_filter && search?.id) {
          inbox.current?.setFeedFilter({
            custom: { search: ['==', search.id.toString()] },
          });
          inbox.current?.onConversationSelected(conversationSelected);
        }
        if (proposalId) {
          const selectedConversation = await restFactory.get<
            TalkjsConversationEntity[]
          >(`/proposals/${proposalId}/conversation`);
          if (selectedConversation[0]?.id) {
            inbox.current?.select(selectedConversation[0].id);
          }
        }
        await inbox.current?.mount(chatContainer.current);
        const iframe = chatContainer?.current?.querySelector('iframe');
        loadContentIFrame(iframe);
      }
    }
  };

  const loadContentIFrame = (iframe: HTMLIFrameElement | null) => {
    if (iframe) {
      iframe.addEventListener('load', () => setIsIframeLoaded(true));
    }
  };

  const sendMessage = (message: string) => {
    if (message) {
      const conversationBuilder = chatSession.getOrCreateConversation(
        conversationInfo.conversation.id
      );
      conversationBuilder.sendMessage(message);
    }
  };

  const onConversationClosed = async (id: string) => {
    // To hide the menu immediately after user clicks Close Conversation btn
    // we update the local state and then we sync with talks object to check
    // the conversation was closed successfully
    setIsClosed(true);
    setIsClosed(getIsConversationClosed(await loadConversationFullData(id)));
  };

  if (user.ghostUserEmail) {
    return (
      <h3 className="pt-5 mt-5 text-danger d-flex align-items-center justify-content-center">
        <BsFillXOctagonFill className="mr-2" />
        Chats are disabled in ghosting mode.
      </h3>
    );
  }

  const renderHeader = () => {
    return (
      <ChatCustomHeader
        key={proposal?.id || search?.id}
        isDataProvider={!!user.user.data_provider}
        subject={conversationInfo?.conversation?.subject || ''}
        proposal={proposal}
        fetchAllProposals={fetchAllProposals as (isBuyer: boolean) => void}
        sendMessage={sendMessage}
        user={user.user}
        conversation={conversationInfo?.conversation}
        showDotsMenu={
          search ? search.review_status === REVIEW_STATUS.APPROVED : !isClosed
        }
        onConversationClosed={onConversationClosed}
        search={search}
        headerLoaded={() => {
          setIsHeaderReady(true);
        }}
        isVisible={isHeaderReady && isIframeLoaded}
      />
    );
  };

  return (
    <div
      className={classnames(
        styles.root,
        {
          [styles.rootNoResponses]: !hasConversations,
        },
        className
      )}
    >
      {loading && (
        <div className="loadingIndicatorContainer">
          <Spinner animation="border" />
        </div>
      )}
      {hasConversations && renderHeader()}
      <div
        className={classnames(styles.chatContainerBox, {
          [styles.withConversations]: hasConversations,
          [styles.iframeLoading]: loading,
        })}
        ref={chatContainer}
        data-cy="talkjs-chat-iframe-container"
      ></div>
    </div>
  );
};

const mapStateToProps = (state: GenericObject) => {
  return {
    chatSession: state.chat.chatSession,
    user: state.user,
    proposals: state.proposals,
  };
};

export default connect<StateProps, DispatchProps, OwnProps>(mapStateToProps, {
  fetchAllProposals,
})(Chat);
